import React, { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'src/theming';
import {
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import Card from 'src/components/Card';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DeliveryOrder, { CheckabledField, OrderArticle, PostDeliveryOrderBody } from 'src/interfaces/api/DeliveryOrder';
import styles from './View.styles';
import { useNotifier } from 'src/components/NotificationCenter/AlertNotificationCenter';
import { MdChevronLeft as BackIcon } from '@react-icons/all-files/md/MdChevronLeft';
import { LegacyDeal, QuoteArticleHardware, QuoteArticleVirtual } from 'src/interfaces/api/Deal';
import useDeal from 'src/hooks/api/useDeal';
import AddressFields from './components/AddressFields';
import DealSelect from './components/DealSelect';
import DomainSelect from 'src/components/ApiField/DomainSelect';
import ProductsSection from './components/ProductsSection';
import { Skeleton } from '@material-ui/lab';
import DatePicker from 'src/components/DatePicker';
import moment from 'moment';
import { plurify } from 'src/utils/StringUtils';
import { BsEyeFill as ViewIcon } from '@react-icons/all-files/bs/BsEyeFill';
import { Link } from 'react-router-dom';
import SignatureField from 'src/scenes/components/SignatureField';
import EmployeeSelect from 'src/components/ApiField/EmployeeSelect';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import Badge from '../../../../components/Badge';
import AskBeforeLeaving from '../../../../components/AskBeforeLeaving';
import api from '../../../../config/api';
import PhotoPicker from '../../../components/PhotoPicker';
import TimePicker from '../../../../components/TimePicker';

const useStyles = createUseStyles(styles);

interface Props {
  onSaveDeliveryOrder: (DeliveryOrder: PostDeliveryOrderBody) => Promise<DeliveryOrder>;
  goBack: () => void;
  deliveryOrder?: DeliveryOrder;
  loading?: boolean;
}

const View: FC<Props> = ({ goBack, loading, deliveryOrder, onSaveDeliveryOrder }) => {
  const classes = useStyles();
  const [domainFilter, setDomainFilter] = useState<number>();
  const [formError, setFormError] = useState<string>();
  const [lessArticlesCount, setLessArticlesCount] = useState(0);
  const { addNotification } = useNotifier();
  const [error, setError] = useState<string>();
  const [hardwareArticles, setHardwareArticles] = useState<QuoteArticleHardware[]>([]);
  const [virtualArticles, setVirtualArticles] = useState<QuoteArticleVirtual[]>([]);
  const [dealLoading, setDealLoading] = useState(false);
  const { getLegacy: getDeal } = useDeal();
  const [isSendConfirmationDialogOpened, setIsSendConfirmationDialogOpened] = useState(false);

  const formMethods = useForm<PostDeliveryOrderBody>({
    defaultValues: {
      attachments: [],
      deliveryDate: moment().format(),
      startTime: moment().format('HH:mm'),
      endTime: moment().format('HH:mm'),
      partialDelivery: false,
      signature: undefined,
    },
  });
  const watchDealId = formMethods.watch('dealId');
  const watchArticles = formMethods.watch('articles');

  const getDealInformation = async (dealId: LegacyDeal['id']): Promise<void> => {
    setDealLoading(true);
    setError(undefined);

    try {
      const productsFields =
        'stepProd { id, intermediateStageNumber, preparation{ installation{ id, trainingSheets{ id, name }, workOrders{ id, name }, images{ id, name }}, associationsProductsHardware{ id, products{ id, installed, dateInstalled, stickerId }, quoteArticle{ id, quantity, commercialWording, productReference{ id, wording, supplierHardware{ name, url } } } },associationsProductsVirtual{ id, products{ id, installed, dateInstalled }, quoteArticle{ id, quantity, commercialWording, productReference{ id, wording, supplierVirtual{ name, url}}}}}}}';
      const deal = await getDeal(dealId, productsFields);

      const productsHardware: CheckabledField<OrderArticle>[] = (
        deal.stepProd?.preparation?.associationsProductsHardware || []
      ).map((item) => ({
        code: item.quoteArticle?.id ? `${item.quoteArticle?.id}` : undefined,
        name: item.quoteArticle?.productReference?.wording || '',
        description: item.quoteArticle?.commercialWording || '',
        productReferenceId: item.quoteArticle?.productReference?.id,
        originalQuantity: item.quoteArticle?.quantity || 0,
        quantity: item.quoteArticle?.quantity || 0,
        enabled: true,
      }));

      const productsVirtual: CheckabledField<OrderArticle>[] = (
        deal.stepProd?.preparation?.associationsProductsVirtual || []
      ).map((item) => ({
        code: item.quoteArticle?.id ? `${item.quoteArticle?.id}` : undefined,
        name: item.quoteArticle?.productReference?.wording || '',
        description: item.quoteArticle?.commercialWording || '',
        productReferenceId: item.quoteArticle?.productReference?.id,
        originalQuantity: item.quoteArticle?.quantity || 0,
        quantity: item.quoteArticle?.quantity || 0,
        enabled: true,
      }));

      if (!deliveryOrder) {
        formMethods.setValue('articles', productsHardware.concat(productsVirtual), { shouldValidate: true });
        formMethods.setValue('socialReason', deal.customer?.installationSite?.socialReason || '', {
          shouldValidate: true,
        });
        formMethods.setValue('address', deal.customer?.installationSite?.adress || '', { shouldValidate: true });
        formMethods.setValue('phoneNumber', deal.customer?.installationSite?.telOffice || '', { shouldValidate: true });
        formMethods.setValue('city', deal.customer?.installationSite?.postalCode || '', { shouldValidate: true });
        formMethods.setValue(
          'contact',
          (deal.interlocutors || [])
            .map((interlocutor) => `${interlocutor.name || ''} ${interlocutor.lastname || ''}`)
            .join(', '),
          { shouldValidate: true }
        );
        formMethods.setValue(
          'recipientEmails',
          (deal.interlocutors || []).map((interlocutor) => interlocutor.email || '').join(', '),
          { shouldValidate: true }
        );

        setLessArticlesCount(0);
      }

      const hardwareProducts: QuoteArticleHardware[] = (deal.stepProd?.preparation?.associationsProductsHardware || [])
        .map((prod) => prod.quoteArticle)
        .filter((prod): prod is QuoteArticleHardware => !!prod);
      setHardwareArticles(hardwareProducts);

      const virtualProducts: QuoteArticleVirtual[] = (deal.stepProd?.preparation?.associationsProductsVirtual || [])
        .map((prod) => prod.quoteArticle)
        .filter((prod): prod is QuoteArticleVirtual => !!prod);
      setVirtualArticles(virtualProducts);

      setDealLoading(false);
    } catch (e) {
      setError(e.message);
      setDealLoading(false);
    }
  };

  useEffect(() => {
    if (deliveryOrder) {
      formMethods.reset(deliveryOrder);
      setLessArticlesCount(0);
    }
  }, [deliveryOrder]);

  useEffect(() => {
    if (watchDealId) {
      getDealInformation(watchDealId);
    }
  }, [watchDealId]);

  useEffect(() => {
    if (watchArticles) {
      let lessQuantity = 0;
      watchArticles.forEach((article) => {
        if (!article.enabled || article.quantity < article.originalQuantity) {
          lessQuantity += article.enabled ? article.originalQuantity - article.quantity : article.originalQuantity;
        }
      });
      setLessArticlesCount(lessQuantity);
    }
  }, [JSON.stringify(watchArticles)]);

  const handleDomainFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    formMethods.resetField('dealId');
    const value = event.target.value as number;
    setDomainFilter(value);
  };

  const onSubmit = async (deliveryOrder: PostDeliveryOrderBody) => {
    try {
      const { articles, ...order } = deliveryOrder;
      const validatedArticles = articles.filter((article) => article.enabled && article.quantity > 0);

      await onSaveDeliveryOrder({ ...order, articles: validatedArticles });

      addNotification({
        title: 'Bon de livraison enregistré avec succès !',
        alertProps: { color: 'success' },
      });

      goBack();
      setLessArticlesCount(0);
      setFormError(undefined);
    } catch (e) {
      setFormError(e.message);
    }
  };

  const handlePreSave = formMethods.handleSubmit((data) => onSubmit({ ...data, toSend: false }));

  const handleSubmit = () => {
    setIsSendConfirmationDialogOpened(false);
    formMethods.handleSubmit((data) => onSubmit({ ...data, toSend: true }))();
  };

  return (
    <FormProvider {...formMethods}>
      <AskBeforeLeaving
        message="Le formulaire n'a pas été enregistré, êtes-vous sûr de vouloir partir ?"
        condition={formMethods.formState.isDirty && !formMethods.formState.isSubmitting}
      />

      <MuiPickersUtilsProvider utils={MomentUtils}>
        <Container className={classes.root} maxWidth='md'>
          <Button className={classes.button} onClick={goBack}>
            <BackIcon size={25} /> Retour à la liste
          </Button>

          <Card>
            <Typography className={classes.title} variant='h5'>
              Bon de livraison
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={5} sm={4}>
                <DomainSelect fullWidth useLegacyId onChange={handleDomainFilterChange} value={domainFilter} />
              </Grid>

              <Grid item xs={7} sm={deliveryOrder?.id && watchDealId ? 7 : 8}>
                <Controller
                  name='dealId'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <DealSelect
                      label='Affaire'
                      domainId={domainFilter}
                      helperText={error?.message}
                      error={undefined !== error}
                      {...field}
                    />
                  )}
                />
              </Grid>

              {deliveryOrder?.id && !!watchDealId && (
                <Hidden xsDown>
                  <Grid item xs={1}>
                    <IconButton component={Link} to={`/dashboard/deal/${watchDealId}`}>
                      <ViewIcon color='#0000008a' />
                    </IconButton>
                  </Grid>
                </Hidden>
              )}

              <Grid item xs={12} sm={4}>
                <Controller
                  name='deliveryDate'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      {...field}
                      label='Date de livraison'
                      format='DD/MM/YYYY'
                      inputVariant='outlined'
                      helperText={error?.message}
                      error={undefined !== error}
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={4}>
                <Controller
                  name='startTime'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <TimePicker
                      {...field}
                      ampm={false}
                      label="Heure d'arrivée"
                      inputVariant='outlined'
                      helperText={error?.message}
                      error={undefined !== error}
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={4}>
                <Controller
                  name='endTime'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <TimePicker
                      {...field}
                      ampm={false}
                      label='Heure de départ'
                      inputVariant='outlined'
                      helperText={error?.message}
                      error={undefined !== error}
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='socialReason'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field: { value, ...others }, fieldState: { error } }) =>
                    dealLoading ? (
                      <Skeleton height={56} />
                    ) : (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Raison sociale'
                        variant='outlined'
                        fullWidth
                        required
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='phoneNumber'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field: { value, ...others }, fieldState: { error } }) =>
                    dealLoading ? (
                      <Skeleton height={56} />
                    ) : (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Téléphone'
                        variant='outlined'
                        fullWidth
                        required
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <AddressFields loading={dealLoading} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='overline' className={classes.section}>
                  Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <ProductsSection virtualArticles={virtualArticles} hardwareArticles={hardwareArticles} />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='attachments'
                  control={formMethods.control}
                  render={({ field: { ref, ...otherField }, fieldState: { error } }) => (
                    <PhotoPicker
                      {...otherField}
                      error={undefined !== error}
                      helperText={error?.message}
                      itemId={deliveryOrder?.id}
                      getItemAttachmentApi={(attachmentId: string) =>
                        api.deliveryOrder.getAttachment(deliveryOrder!.id, attachmentId)
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='overline' className={classes.section}>
                  Informations complementaires
                </Typography>

                {lessArticlesCount > 0 && (
                  <Grid item xs={12}>
                    <Typography variant='caption' className={classes.section}>
                      Il y a {lessArticlesCount} {plurify('article', lessArticlesCount)} manquant dans cette commande.
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='partialDelivery'
                  control={formMethods.control}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <FormControlLabel
                      control={<Checkbox size='small' />}
                      label='Livraison partielle'
                      checked={value}
                      {...field}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='observations'
                  control={formMethods.control}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Observations'
                      variant='outlined'
                      multiline
                      value={value || ''}
                      minRows={3}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name='contact'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field: { value, ...others }, fieldState: { error } }) =>
                    dealLoading ? (
                      <Skeleton height={56} />
                    ) : (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Contact'
                        variant='outlined'
                        fullWidth
                        required
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name='intervenantId'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <EmployeeSelect
                      {...field}
                      multiple={false}
                      fullWidth
                      label="Nom de l'intervenant"
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                {deliveryOrder?.signatureId ? (
                  <Badge color='primary' text='Signature déjà enregistré !' />
                ) : (
                  <SignatureField />
                )}
              </Grid>

              <Grid className={classes.column} item xs={12}>
                <Typography variant='overline' className={classes.section}>
                  Envoyez le bon de livraison aux adresses suivantes
                </Typography>

                <Typography variant='caption' className={classes.section}>
                  Séparez les mails par des virgules
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='recipientEmails'
                  control={formMethods.control}
                  render={({ field: { value, ...others }, fieldState: { error } }) => (
                    <TextField
                      {...others}
                      value={value || ''}
                      label='Adresses mails'
                      variant='outlined'
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              {formError && (
                <Grid item xs={12}>
                  <p className={classes.error}>{formError}</p>
                </Grid>
              )}

              {!deliveryOrder?.sended && (
                <>
                  <Grid item xs={8}>
                    <Button
                      fullWidth
                      color='primary'
                      variant='outlined'
                      disabled={formMethods.formState.isSubmitting}
                      onClick={handlePreSave}
                    >
                      Pre-enregistrer
                    </Button>
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      color='primary'
                      variant='contained'
                      disabled={formMethods.formState.isSubmitting}
                      onClick={() => setIsSendConfirmationDialogOpened(true)}
                    >
                      Envoyer
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>

            {(loading || formMethods.formState.isSubmitting) && <LinearProgress style={{ marginTop: 5 }} />}
          </Card>
        </Container>

        <ConfirmationDialog
          open={isSendConfirmationDialogOpened}
          title='Attention'
          description={`Vous êtes sur le point d'envoyer le bon de livraison.\nIl sera ajouté sur le mur du client puis envoyé aux adresses que vous avez défini.\nUne fois envoyé, il vous sera impossible de l'editer.`}
          confirmationText='Valider'
          onValidate={handleSubmit}
          onClose={() => setIsSendConfirmationDialogOpened(false)}
        />
      </MuiPickersUtilsProvider>
    </FormProvider>
  );
};

export default View;
